import classNames from "classnames";
import { PRIO, STATUS } from "../../lib/design";
import { formatDay, todayKey } from "../../lib/date";
import { QuickAdd } from "../QuickAdd";
import type { LayoutProps } from "./ListLayout";

const DAY_MS = 86_400_000;

function toMs(dayKey: string): number {
  const [y, m, d] = dayKey.split("-").map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d, 12);
}

function shift(dayKey: string, days: number): string {
  return new Date(toMs(dayKey) + days * DAY_MS).toISOString().slice(0, 10);
}

/** Layout Timeline/Gantt — barres sur fenêtre temporelle dérivée des données.
 * ponytail: TmTask n'a pas de date de début → barre created_at→due_date
 * (barre d'un jour si création postérieure à l'échéance) ; vraie date de
 * début quand TM l'exposera. */
export function TimelineLayout({
  groups,
  selectedTaskId,
  onSelect,
  composerFor,
  composerBusy,
}: Omit<LayoutProps, "onToggleDone" | "canDrop" | "onDropTask">) {
  const today = todayKey();

  // Fenêtre : bornée par les échéances visibles, clampée autour d'aujourd'hui.
  const dated = groups.flatMap((g) => g.items).filter((t) => t.dueKey);
  let start = shift(today, -7);
  let end = shift(today, 21);
  for (const t of dated) {
    if (t.dueKey! < start) start = t.dueKey!;
    if (t.dueKey! > end) end = t.dueKey!;
  }
  if (start < shift(today, -45)) start = shift(today, -45);
  if (end > shift(today, 90)) end = shift(today, 90);
  const span = toMs(end) - toMs(start) + DAY_MS;
  const pct = (k: string) => ((toMs(k) - toMs(start)) / span) * 100;
  const todayPct = pct(today);

  // Graduations hebdomadaires.
  const ticks: string[] = [];
  for (let k = start; k <= end; k = shift(k, 7)) ticks.push(k);

  const scale = (
    <div className="tl-scale">
      <div className="tl-left" />
      <div className="tl-track">
        {ticks.map((k) => (
          <span key={k} className="tl-tick" style={{ left: `${pct(k)}%` }}>
            {formatDay(k)}
          </span>
        ))}
        {todayPct >= 0 && todayPct <= 100 && (
          <span className="tl-today" style={{ left: `${todayPct}%` }} />
        )}
      </div>
    </div>
  );

  return (
    <div className="timeline">
      {scale}
      {groups.map((g) => {
        const undated = g.items.filter((t) => !t.dueKey);
        const onAdd = composerFor(g.key);
        return (
          <section key={g.key}>
            <div
              className="group-title"
              style={g.color ? { color: g.color } : undefined}
            >
              {g.color && (
                <span className="group-dot" style={{ background: g.color }} />
              )}
              {g.label}
              <span style={{ fontWeight: 500 }}>{g.items.length}</span>
            </div>
            {g.items
              .filter((t) => t.dueKey)
              .map((t) => {
                const from =
                  t.createdKey && t.createdKey < t.dueKey!
                    ? t.createdKey
                    : t.dueKey!;
                const left = Math.max(0, pct(from));
                const width = Math.max(
                  100 / (span / DAY_MS),
                  Math.min(100, pct(t.dueKey!) + 100 / (span / DAY_MS)) - left,
                );
                return (
                  <div
                    key={t.id}
                    className={classNames("tl-row", {
                      selected: t.id === selectedTaskId,
                    })}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      onSelect(t.id === selectedTaskId ? null : t.id)
                    }
                    onKeyDown={(e) =>
                      e.key === "Enter" &&
                      onSelect(t.id === selectedTaskId ? null : t.id)
                    }
                  >
                    <div className="tl-left">
                      <span
                        className="group-dot"
                        style={{ background: STATUS[t.status].dot }}
                      />
                      <span className="tl-title">{t.title}</span>
                      <span
                        className="chip"
                        style={{
                          color: PRIO[t.priority].color,
                          background: PRIO[t.priority].bg,
                        }}
                      >
                        {PRIO[t.priority].label}
                      </span>
                    </div>
                    <div className="tl-track">
                      {todayPct >= 0 && todayPct <= 100 && (
                        <span
                          className="tl-today"
                          style={{ left: `${todayPct}%` }}
                        />
                      )}
                      <span
                        className="tl-bar"
                        style={{
                          left: `${left}%`,
                          width: `${width}%`,
                          background: STATUS[t.status].dot,
                          opacity: t.status === "done" ? 0.45 : 0.85,
                        }}
                        title={`${formatDay(from)} → ${formatDay(t.dueKey!)}`}
                      />
                    </div>
                  </div>
                );
              })}
            {undated.length > 0 && (
              <div className="toolbar-hint" style={{ paddingLeft: 4 }}>
                + {undated.length} tâche{undated.length > 1 ? "s" : ""} sans
                échéance
              </div>
            )}
            {onAdd && (
              <div className="group-composer" style={{ maxWidth: 340 }}>
                <QuickAdd
                  placeholder={`Ajouter dans « ${g.label} »…`}
                  onAdd={onAdd}
                  busy={composerBusy}
                />
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
